import {
  Fragment,
  type FC
} from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  isDefined
} from '@shared/lib';
import {
  UserRegistrationStatusEnum
} from '@shared/types';
import {
  Link,
  Stack,
  Button,
  Typography,
  styled,
} from '@mui/joy';
import {
  selectKycStatus,
  useSessionSelector,
  selectRegistrationStatus,
  UserKycStatusEnum,
} from '@entities/session';

const BannerContainer = styled(Stack)(({ theme }) => ({
  gap: 24,
  width: '100%',
  paddingBlock: 8,
  paddingInline: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common[1065],
  [theme.breakpoints.down(491)]: {
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingBlock: 4,
    paddingInline: 12,
  },
}));

const BannerDescriptionContainer = styled(Stack)(({ theme }) => ({
  flexWrap: 'wrap',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.25rem',
  [theme.breakpoints.down(491)]: { flex: 1 }
}));

type BannerDescriptionProps = {
  title: string;
  linkHref: string;
  linkText: string;
  description: string;
};
const BannerDescription: FC<BannerDescriptionProps> = ({ title, description, linkHref, linkText }) => (
  <BannerDescriptionContainer>
    <Typography
      component='span'
      sx={({ palette, breakpoints }) => ({
        display: 'inline',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: '20px',
        fontStyle: 'normal',
        whiteSpace: 'nowrap',
        color: palette.common[565],
        [breakpoints.down(491)]: { fontSize: 12 },
      })}>
      {title}
    </Typography>
    <Typography
      component='span'
      sx={({ palette, breakpoints }) => ({
        display: 'inline',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '20px',
        fontStyle: 'normal',
        color: palette.common[565],
        [breakpoints.down(491)]: { fontSize: 12 },
      })}>
      {description}
    </Typography>
    <Link
      href={linkHref}
      sx={({ palette, breakpoints }) => ({
        display: 'inline',
        lineHeight: 'normal',
        color: palette.common[565],
        textDecoration: 'underline',
        whiteSpace: 'nowrap',
        ['&:hover']: {
          color: palette.common[565],
          textDecoration: palette.common[565],
        },
        [breakpoints.down(491)]: { fontSize: 12 },
      })}>
      {linkText}
    </Link>
  </BannerDescriptionContainer>
);

export const UserVerificationBanner: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    kycStatus,
    registrationStatus
  } = useSessionSelector(state => ({
    kycStatus: selectKycStatus(state),
    registrationStatus: selectRegistrationStatus(state),
  }));

  const allowedKycBanner =
    isDefined(kycStatus) &&
    isDefined(registrationStatus) &&
    kycStatus !== UserKycStatusEnum.Pass &&
    registrationStatus === UserRegistrationStatusEnum.Player;

  const allowedPlayerRegistrationBanner = isDefined(registrationStatus) &&
    registrationStatus !== UserRegistrationStatusEnum.Player;

  const allowedBanner = (allowedKycBanner || allowedPlayerRegistrationBanner)
    && !location.pathname?.includes('/games/');

  const handleVerify = (): void => {
    if (allowedKycBanner) navigate('/account/documents');
    else if (allowedPlayerRegistrationBanner) navigate('/account/profile');
    else console.warn('No action available');
  };

  return allowedBanner && (
    <BannerContainer>
      {allowedKycBanner && (
        <Fragment>
          <BannerDescription
            linkHref='/terms-and-conditions'
            linkText='Why do I need to verify?'
            title='Complete quick verification.'
            description='Quick and secure: verify once to unlock full access. '
          />
          <Button
            color='slate'
            variant='outlined'
            onClick={handleVerify}
            sx={({ breakpoints }) => ({
              paddingBlock: '.25rem',
              paddingInline: '2rem',
              fontWeight: 'var(--joy-fontWeight-lg, 500)',
              minHeight: 'var(--Button-minHeight, 2.25rem)',
              flexShrink: 0,
              alignSelf: 'center',
              [breakpoints.down(491)]: { alignSelf: 'center', },
            })}>
            Verify Now
          </Button>
        </Fragment>
      )}
      {allowedPlayerRegistrationBanner && (
        <Fragment >
          <BannerDescription
            linkHref='/terms-and-conditions'
            title='Start your verification journey.'
            linkText='Why do I need to complete my profile?'
            description='Complete your profile to unlock identity verification and full access. '
          />
          <Button
            color='slate'
            variant='outlined'
            onClick={handleVerify}
            sx={({ breakpoints }) => ({
              paddingBlock: '.25rem',
              paddingInline: '0.75rem',
              fontWeight: 'var(--joy-fontWeight-lg, 500)',
              minHeight: 'var(--Button-minHeight, 2.25rem)',
              flexShrink: 0,
              alignSelf: 'center',
              [breakpoints.down(491)]: { alignSelf: 'center' },
            })}>
            Complete Profile
          </Button>
        </Fragment>
      )}
    </BannerContainer>
  );
};